import React from 'react';
import { Link } from 'react-router-dom';

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Header Section */}
          <div className="mb-12">
            <h1 className="text-3xl font-extrabold bg-clip-text mb-6">
              Algorithm Trading Platform
            </h1>
          </div>

          {/* Strategy Concept Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-100">
              <h2 className="text-1xl font-bold text-gray-900 mb-8 text-left">
                Strategy Core Concepts
              </h2>
              <ul className="text-gray-700 space-y-6 text-left">
                <li className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-bold text-sm">1</span>
                  </div>
                  <div className="flex-1">
                    <span className="text-md leading-relaxed">
                      On the <code className="bg-gray-100 px-2 py-1 rounded text-blue-600 font-mono text-sm">1d</code> timeframe, once the daily candlestick closes, the platform will determine the directional bias for each ticker.
                    </span>
                  </div>
                </li>
                <li className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-bold text-sm">2</span>
                  </div>
                  <div className="flex-1">
                    <span className="text-md leading-relaxed">
                      Throughout the day, after the close of each <code className="bg-gray-100 px-2 py-1 rounded text-purple-600 font-mono text-sm">15m</code> candlestick, the platform will immediately check whether the current trend aligns with the daily bias.
                    </span>
                  </div>
                </li>
                <li className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold text-sm">3</span>
                  </div>
                  <div className="flex-1">
                    <span className="text-md leading-relaxed">
                      When a <code className="bg-gray-100 px-2 py-1 rounded text-green-600 font-mono text-sm">5m</code> candlestick closes and the most recent <code className="bg-gray-100 px-2 py-1 rounded text-green-600 font-mono text-sm">15m</code> candlestick is aligned with the daily bias, the platform will evaluate whether the <code className="bg-gray-100 px-2 py-1 rounded text-green-600 font-mono text-sm">5m</code> candlestick shows a valid trigger in that direction.
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Navigation Cards Section */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Link
              to="/bias"
              className="group relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl hover:scale-105 transition-all duration-300 hover:border-blue-200"
            >
              <div className="absolute top-4 right-4 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors duration-300">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">Bias</h2>
              <p className="text-gray-600 leading-relaxed">Explore the daily directional bias for each ticker.</p>
            </Link>

            <Link
              to="/trends"
              className="group relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl hover:scale-105 transition-all duration-300 hover:border-purple-200"
            >
              <div className="absolute top-4 right-4 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center group-hover:bg-purple-200 transition-colors duration-300">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-purple-600 transition-colors duration-300">Trends</h2>
              <p className="text-gray-600 leading-relaxed">Monitor real-time <code className="bg-gray-100 px-2 py-1 rounded text-purple-600 font-mono">15m</code> trend confirmations.</p>
            </Link>

            <Link
              to="/triggers"
              className="group relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl hover:scale-105 transition-all duration-300 hover:border-green-200"
            >
              <div className="absolute top-4 right-4 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200 transition-colors duration-300">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-green-600 transition-colors duration-300">Triggers</h2>
              <p className="text-gray-600 leading-relaxed">Check real-time <code className="bg-gray-100 px-2 py-1 rounded text-green-600 font-mono">5m</code> trigger signals.</p>
            </Link>

            <Link
              to="/corner-to-corner"
              className="group relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl hover:scale-105 transition-all duration-300 hover:border-red-200"
            >
              <div className="absolute top-4 right-4 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center group-hover:bg-red-200 transition-colors duration-300">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-red-600 transition-colors duration-300">Corner to Corner</h2>
              <p className="text-gray-600 leading-relaxed">Check real-time <code className="bg-gray-100 px-2 py-1 rounded text-red-600 font-mono">15m</code> corner to corner signals.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage; 